import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDropzone } from 'react-dropzone';
import {
  ArrowLeftIcon,
  CloudArrowUpIcon,
  TrashIcon,
  ArrowPathIcon,
  DocumentArrowDownIcon,
  TableCellsIcon,
  MapIcon,
  MagnifyingGlassIcon,
  SparklesIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ChevronRightIcon,
  ChartBarIcon,
  BellAlertIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { larBuilderApi } from '../lib/api';
import { StatusBadge } from '../components/ui';

// ── Source System Options ────────────────────────────────────────────────────

const SOURCE_SYSTEMS = [
  { value: 'JACK_HENRY_SILVERLAKE', label: 'Jack Henry SilverLake' },
  { value: 'JACK_HENRY_CIF2020', label: 'Jack Henry CIF 20/20' },
  { value: 'JACK_HENRY_SYMITAR', label: 'Jack Henry Symitar' },
  { value: 'LASERPRO', label: 'LaserPro (Finastra)' },
  { value: 'FISERV_DNA', label: 'Fiserv DNA' },
  { value: 'FISERV_PRECISION', label: 'Fiserv Precision' },
  { value: 'FIS_HORIZON', label: 'FIS Horizon' },
  { value: 'ENCOMPASS', label: 'Encompass (ICE)' },
  { value: 'GENERIC_CSV', label: 'Generic CSV' },
  { value: 'MANUAL', label: 'Manual Entry' },
];

const TABS = [
  { id: 'sources', label: 'Sources', icon: CloudArrowUpIcon },
  { id: 'map', label: 'Map Fields', icon: MapIcon },
  { id: 'review', label: 'Review & Fix', icon: MagnifyingGlassIcon },
  { id: 'auto-populate', label: 'Auto-Populate', icon: SparklesIcon },
  { id: 'monitor', label: 'Monitor', icon: ChartBarIcon },
  { id: 'export', label: 'Export', icon: DocumentArrowDownIcon },
];

// ── HMDA Field names for display ─────────────────────────────────────────────

const FIELD_NAMES: Record<number, string> = {
  1: 'Record Identifier', 2: 'LEI', 3: 'ULI', 4: 'Application Date',
  5: 'Loan Type', 6: 'Loan Purpose', 7: 'Preapproval', 8: 'Construction Method',
  9: 'Occupancy Type', 10: 'Loan Amount', 11: 'Action Taken', 12: 'Action Taken Date',
  13: 'Street Address', 14: 'City', 15: 'State', 16: 'Zip Code',
  17: 'County', 18: 'Census Tract', 19: 'Ethnicity Applicant 1',
  25: 'Ethnicity Co-Applicant 1', 31: 'Ethnicity Observed (Applicant)',
  32: 'Ethnicity Observed (Co-Applicant)', 33: 'Race Applicant 1',
  39: 'Race Co-Applicant 1', 45: 'Race Observed (Applicant)',
  46: 'Race Observed (Co-Applicant)', 47: 'Sex (Applicant)', 48: 'Sex (Co-Applicant)',
  49: 'Sex Observed (Applicant)', 50: 'Sex Observed (Co-Applicant)',
  51: 'Age (Applicant)', 52: 'Age (Co-Applicant)', 53: 'Income',
  54: 'Purchaser Type', 55: 'Rate Spread', 56: 'HOEPA Status', 57: 'Lien Status',
  58: 'Credit Score (Applicant)', 59: 'Credit Score (Co-Applicant)',
  60: 'Credit Score Model (Applicant)', 62: 'Credit Score Model (Co-Applicant)',
  64: 'Denial Reason 1', 69: 'Total Loan Costs', 74: 'Interest Rate',
  76: 'DTI', 77: 'CLTV', 78: 'Loan Term', 80: 'Balloon Payment',
  81: 'Interest-Only', 82: 'Negative Amortization', 83: 'Other Non-Amortizing',
  84: 'Property Value', 85: 'Manufactured Home Secured Property Type',
  86: 'Manufactured Home Land Property Interest', 87: 'Total Units',
  89: 'Application Channel', 90: 'Payable to Institution', 91: 'NMLSR Identifier',
  92: 'AUS 1', 98: 'AUS Result 1', 104: 'Reverse Mortgage',
  105: 'Open-End Line of Credit', 106: 'Business/Commercial Purpose',
};

export default function LarWorkspacePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState('sources');
  const [sourceSystem, setSourceSystem] = useState('GENERIC_CSV');
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [recordsPage, setRecordsPage] = useState(1);
  const [recordsFilter, setRecordsFilter] = useState<string>('');

  // ── Data queries ─────────────────────────────────────────────────────────

  const { data: workspace, isLoading } = useQuery({
    queryKey: ['lar-workspace', id],
    queryFn: async () => {
      const res = await larBuilderApi.getWorkspace(id!);
      return res.data;
    },
    enabled: !!id,
  });

  const { data: templatesData } = useQuery({
    queryKey: ['lar-templates'],
    queryFn: async () => {
      const res = await larBuilderApi.getTemplates();
      return res.data;
    },
  });

  const { data: recordsData, isLoading: recordsLoading } = useQuery({
    queryKey: ['lar-records', id, recordsPage, recordsFilter],
    queryFn: async () => {
      const params: any = { page: recordsPage, limit: 50 };
      if (recordsFilter) params.status = recordsFilter;
      const res = await larBuilderApi.getRecords(id!, params);
      return res.data;
    },
    enabled: !!id && (activeTab === 'review'),
  });

  const templates = templatesData?.templates || [];
  const records = recordsData?.records || [];
  const pagination = recordsData?.pagination;

  // ── Mutations ────────────────────────────────────────────────────────────

  const uploadMutation = useMutation({
    mutationFn: ({ file, system }: { file: File; system: string }) =>
      larBuilderApi.uploadSource(id!, file, system),
    onSuccess: () => {
      toast.success('File uploaded');
      queryClient.invalidateQueries({ queryKey: ['lar-workspace', id] });
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Upload failed'),
  });

  const deleteSourceMutation = useMutation({
    mutationFn: (sourceId: string) => larBuilderApi.deleteSource(id!, sourceId),
    onSuccess: () => {
      toast.success('Source file removed');
      queryClient.invalidateQueries({ queryKey: ['lar-workspace', id] });
    },
  });

  const mapMutation = useMutation({
    mutationFn: ({ sourceId, templateId }: { sourceId: string; templateId: string }) =>
      larBuilderApi.mapSource(id!, sourceId, templateId),
    onSuccess: (res) => {
      toast.success(`Mapped ${res.data.mappedRows} rows`);
      queryClient.invalidateQueries({ queryKey: ['lar-workspace', id] });
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Mapping failed'),
  });

  const mergeMutation = useMutation({
    mutationFn: () => larBuilderApi.mergeRecords(id!),
    onSuccess: (res) => {
      toast.success(`Merged ${res.data.mergedRecords} duplicate records`);
      queryClient.invalidateQueries({ queryKey: ['lar-workspace', id] });
      queryClient.invalidateQueries({ queryKey: ['lar-records', id] });
    },
  });

  const autoPopulateMutation = useMutation({
    mutationFn: () => larBuilderApi.autoPopulate(id!),
    onSuccess: (res) => {
      const d = res.data;
      toast.success(
        `Auto-populated: ${d.geocoded} geocoded, ${d.rateSpreadCalculated} rate spreads, ${d.uliGenerated} ULIs, ${d.naExemptFilled} NA/Exempt`,
      );
      queryClient.invalidateQueries({ queryKey: ['lar-workspace', id] });
      queryClient.invalidateQueries({ queryKey: ['lar-records', id] });
    },
  });

  const gapsMutation = useMutation({
    mutationFn: () => larBuilderApi.analyzeGaps(id!),
    onSuccess: (res) => {
      const d = res.data;
      toast.success(`Gap analysis complete: ${d.complete} complete, ${d.hasGaps} with gaps`);
      queryClient.invalidateQueries({ queryKey: ['lar-workspace', id] });
      queryClient.invalidateQueries({ queryKey: ['lar-records', id] });
    },
  });

  const exportMutation = useMutation({
    mutationFn: () => larBuilderApi.exportLar(id!),
    onSuccess: (res) => {
      const blob = new Blob([res.data], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `LAR_${workspace?.filingYear || 'export'}.txt`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('LAR file downloaded');
      queryClient.invalidateQueries({ queryKey: ['lar-workspace', id] });
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Export failed'),
  });

  // Phase 1B: Validation + Fair Lending
  const { data: validationData } = useQuery({
    queryKey: ['lar-validation', id],
    queryFn: async () => {
      const res = await larBuilderApi.getValidation(id!);
      return res.data;
    },
    enabled: !!id && activeTab === 'export',
  });

  const validateMutation = useMutation({
    mutationFn: () => larBuilderApi.validateLar(id!),
    onSuccess: (res) => {
      const s = res.data.summary;
      toast.success(
        `Edit checks complete: ${s.totalIssues} issues (${s.syntacticalErrors} syntactical, ${s.validityErrors} validity, ${s.qualityWarnings} quality)`,
      );
      queryClient.invalidateQueries({ queryKey: ['lar-workspace', id] });
      queryClient.invalidateQueries({ queryKey: ['lar-validation', id] });
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Validation failed'),
  });

  const fairLendingMutation = useMutation({
    mutationFn: () => larBuilderApi.runFairLending(id!),
    onSuccess: () => {
      toast.success('Fair lending analysis complete');
      queryClient.invalidateQueries({ queryKey: ['lar-workspace', id] });
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Fair lending analysis failed'),
  });

  const resolveConflictMutation = useMutation({
    mutationFn: ({ recordId, fieldNumber, resolvedValue }: { recordId: string; fieldNumber: number; resolvedValue: string }) =>
      larBuilderApi.resolveConflict(id!, recordId, fieldNumber, resolvedValue),
    onSuccess: () => {
      toast.success('Conflict resolved');
      queryClient.invalidateQueries({ queryKey: ['lar-records', id] });
      queryClient.invalidateQueries({ queryKey: ['lar-workspace', id] });
    },
  });

  // Phase 2: Collection Monitor queries
  const { data: readinessData } = useQuery({
    queryKey: ['lar-readiness', id],
    queryFn: async () => {
      const res = await larBuilderApi.getReadiness(id!);
      return res.data;
    },
    enabled: !!id && activeTab === 'monitor',
  });

  const { data: remindersData } = useQuery({
    queryKey: ['lar-reminders', id],
    queryFn: async () => {
      const res = await larBuilderApi.getReminders(id!);
      return res.data;
    },
    enabled: !!id && activeTab === 'monitor',
  });

  const sendRemindersMutation = useMutation({
    mutationFn: (level?: number) => larBuilderApi.sendReminders(id!, level),
    onSuccess: (res) => {
      toast.success(`Sent ${res.data.newReminders} new reminder(s)`);
      queryClient.invalidateQueries({ queryKey: ['lar-reminders', id] });
      queryClient.invalidateQueries({ queryKey: ['lar-readiness', id] });
    },
    onError: (err: any) => toast.error(err.response?.data?.error || 'Failed to send reminders'),
  });

  const resolveReminderMutation = useMutation({
    mutationFn: (reminderId: string) => larBuilderApi.resolveReminder(id!, reminderId),
    onSuccess: () => {
      toast.success('Reminder resolved');
      queryClient.invalidateQueries({ queryKey: ['lar-reminders', id] });
    },
  });

  // ── File drop handler ────────────────────────────────────────────────────

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        uploadMutation.mutate({ file: acceptedFiles[0], system: sourceSystem });
      }
    },
    [sourceSystem, uploadMutation],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'text/plain': ['.txt'],
    },
    maxFiles: 1,
    maxSize: 100 * 1024 * 1024,
  });

  // ── Loading/error states ─────────────────────────────────────────────────

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">Loading workspace...</div>;
  }
  if (!workspace) {
    return <div className="text-center py-12 text-gray-500">Workspace not found</div>;
  }

  const sourceFiles = workspace.sourceFiles || [];

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate('/hmda/lar-builder')}
          className="rounded-lg border border-gray-300 p-2 hover:bg-gray-50 transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4 text-gray-600" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{workspace.name}</h1>
          <p className="text-sm text-gray-500">Filing Year {workspace.filingYear}</p>
        </div>
        <StatusBadge variant={workspace.status === 'EXPORTED' || workspace.status === 'VALIDATED' ? 'success' : workspace.status === 'REVIEWING' ? 'warning' : 'info'}>
          {workspace.status}
        </StatusBadge>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <SummaryCard label="Total Records" value={workspace.totalRecords} />
        <SummaryCard label="Complete" value={workspace.completeRecords} color="green" />
        <SummaryCard label="Has Gaps" value={workspace.gapRecords} color="amber" />
        <SummaryCard label="Conflicts" value={workspace.conflictRecords} color="red" />
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-6">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 py-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-ocean-600 text-ocean-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      {activeTab === 'sources' && (
        <SourcesTab
          sourceFiles={sourceFiles}
          sourceSystem={sourceSystem}
          setSourceSystem={setSourceSystem}
          getRootProps={getRootProps}
          getInputProps={getInputProps}
          isDragActive={isDragActive}
          isUploading={uploadMutation.isPending}
          onDeleteSource={(sid: string) => deleteSourceMutation.mutate(sid)}
          templates={templates}
          onMapSource={(sourceId: string, templateId: string) =>
            mapMutation.mutate({ sourceId, templateId })
          }
          isMapping={mapMutation.isPending}
        />
      )}

      {activeTab === 'map' && (
        <MapTab
          sourceFiles={sourceFiles}
          templates={templates}
          onMapSource={(sourceId: string, templateId: string) =>
            mapMutation.mutate({ sourceId, templateId })
          }
          isMapping={mapMutation.isPending}
          onMerge={() => mergeMutation.mutate()}
          isMerging={mergeMutation.isPending}
          totalRecords={workspace.totalRecords}
        />
      )}

      {activeTab === 'review' && (
        <ReviewTab
          records={records}
          pagination={pagination}
          isLoading={recordsLoading}
          filter={recordsFilter}
          setFilter={setRecordsFilter}
          page={recordsPage}
          setPage={setRecordsPage}
          selectedRecord={selectedRecord}
          setSelectedRecord={setSelectedRecord}
          onAnalyzeGaps={() => gapsMutation.mutate()}
          isAnalyzing={gapsMutation.isPending}
          workspace={workspace}
          onResolveConflict={(recordId: string, fieldNumber: number, resolvedValue: string) =>
            resolveConflictMutation.mutate({ recordId, fieldNumber, resolvedValue })
          }
          isResolvingConflict={resolveConflictMutation.isPending}
        />
      )}

      {activeTab === 'auto-populate' && (
        <AutoPopulateTab
          workspace={workspace}
          onAutoPopulate={() => autoPopulateMutation.mutate()}
          isPopulating={autoPopulateMutation.isPending}
          result={autoPopulateMutation.data?.data}
        />
      )}

      {activeTab === 'monitor' && (
        <MonitorTab
          workspace={workspace}
          readiness={readinessData}
          reminders={remindersData?.reminders || []}
          onSendReminders={(level?: number) => sendRemindersMutation.mutate(level)}
          isSendingReminders={sendRemindersMutation.isPending}
          onResolveReminder={(rid: string) => resolveReminderMutation.mutate(rid)}
        />
      )}

      {activeTab === 'export' && (
        <ExportTab
          workspace={workspace}
          onExport={() => exportMutation.mutate()}
          isExporting={exportMutation.isPending}
          onUpdateTs={(tsData: any) => {
            larBuilderApi.updateWorkspace(id!, { transmittalSheet: tsData }).then(() => {
              toast.success('Transmittal sheet saved');
              queryClient.invalidateQueries({ queryKey: ['lar-workspace', id] });
            });
          }}
          validationData={validationData}
          onValidate={() => validateMutation.mutate()}
          isValidating={validateMutation.isPending}
          onFairLending={() => fairLendingMutation.mutate()}
          isFairLending={fairLendingMutation.isPending}
        />
      )}
    </div>
  );
}

// ── Summary Card ─────────────────────────────────────────────────────────────

function SummaryCard({ label, value, color }: { label: string; value: number; color?: string }) {
  const colorClass =
    color === 'green' ? 'text-green-600' :
    color === 'amber' ? 'text-amber-600' :
    color === 'red' ? 'text-red-600' :
    'text-gray-900';

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</div>
      <div className={`text-2xl font-bold mt-1 ${colorClass}`}>{value}</div>
    </div>
  );
}

// ── Tab 1: Sources ───────────────────────────────────────────────────────────

function SourcesTab({
  sourceFiles, sourceSystem, setSourceSystem, getRootProps, getInputProps,
  isDragActive, isUploading, onDeleteSource, templates, onMapSource, isMapping,
}: any) {
  return (
    <div className="space-y-6">
      {/* Source system selector + dropzone */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Upload Source File</h3>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">Source System</label>
          <select
            value={sourceSystem}
            onChange={(e) => setSourceSystem(e.target.value)}
            className="w-full max-w-xs rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:ring-1 focus:ring-ocean-500"
          >
            {SOURCE_SYSTEMS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
            isDragActive ? 'border-ocean-500 bg-ocean-50' : 'border-gray-300 hover:border-gray-400'
          }`}
        >
          <input {...getInputProps()} />
          <CloudArrowUpIcon className="h-10 w-10 text-gray-400 mx-auto mb-3" />
          {isUploading ? (
            <p className="text-sm text-ocean-600 font-medium">Uploading...</p>
          ) : isDragActive ? (
            <p className="text-sm text-ocean-600 font-medium">Drop file here</p>
          ) : (
            <>
              <p className="text-sm text-gray-600">Drag & drop a CSV or XLSX file, or click to browse</p>
              <p className="text-xs text-gray-400 mt-1">Max 100 MB</p>
            </>
          )}
        </div>
      </div>

      {/* Uploaded files list */}
      {sourceFiles.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-3 border-b border-gray-200 bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-900">Uploaded Files</h3>
          </div>
          <div className="divide-y divide-gray-200">
            {sourceFiles.map((sf: any) => (
              <div key={sf.id} className="px-6 py-4 flex items-center justify-between">
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900">{sf.fileName}</div>
                  <div className="text-xs text-gray-500 flex gap-3 mt-1">
                    <span>{SOURCE_SYSTEMS.find((s) => s.value === sf.sourceSystem)?.label || sf.sourceSystem}</span>
                    <span>{(sf.fileSize / 1024).toFixed(0)} KB</span>
                    {sf.totalRows > 0 && <span>{sf.totalRows} rows</span>}
                    {sf.detectedColumns?.length > 0 && <span>{sf.detectedColumns.length} columns</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {sf.mappingTemplateId ? (
                    <StatusBadge variant="success">Mapped</StatusBadge>
                  ) : templates.length > 0 ? (
                    <select
                      onChange={(e) => {
                        if (e.target.value) onMapSource(sf.id, e.target.value);
                      }}
                      className="rounded-lg border border-gray-300 px-2 py-1 text-xs"
                      disabled={isMapping}
                      defaultValue=""
                    >
                      <option value="" disabled>Apply template...</option>
                      {templates.map((t: any) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  ) : null}
                  <button
                    onClick={() => onDeleteSource(sf.id)}
                    className="text-gray-400 hover:text-red-600 transition-colors"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tab 2: Map Fields ────────────────────────────────────────────────────────

function MapTab({ sourceFiles, templates, onMapSource, isMapping, onMerge, isMerging, totalRecords }: any) {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Field Mapping</h3>
        <p className="text-sm text-gray-600 mb-4">
          Apply a mapping template to each source file to map its columns to HMDA LAR field positions.
        </p>

        {sourceFiles.length === 0 ? (
          <p className="text-sm text-gray-400">No source files uploaded yet. Go to the Sources tab first.</p>
        ) : (
          <div className="space-y-3">
            {sourceFiles.map((sf: any) => (
              <div key={sf.id} className="flex items-center justify-between border border-gray-200 rounded-lg p-4">
                <div>
                  <div className="text-sm font-medium text-gray-900">{sf.fileName}</div>
                  <div className="text-xs text-gray-500">
                    {sf.detectedColumns?.length || 0} columns detected
                    {sf.mappedRows > 0 && ` · ${sf.mappedRows} rows mapped`}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {sf.mappingTemplateId ? (
                    <StatusBadge variant="success">Mapped</StatusBadge>
                  ) : (
                    <select
                      onChange={(e) => {
                        if (e.target.value) onMapSource(sf.id, e.target.value);
                      }}
                      className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm"
                      disabled={isMapping}
                      defaultValue=""
                    >
                      <option value="" disabled>Select template...</option>
                      {templates.map((t: any) => (
                        <option key={t.id} value={t.id}>{t.name}</option>
                      ))}
                    </select>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Merge section */}
      {sourceFiles.length > 1 && totalRecords > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-2">Merge Records</h3>
          <p className="text-sm text-gray-600 mb-4">
            Combine records from multiple source files. Matching is done by loan identifier (ULI).
            When two sources provide different values for the same field, a conflict will be flagged.
          </p>
          <button
            onClick={onMerge}
            disabled={isMerging}
            className="inline-flex items-center gap-2 rounded-lg bg-ocean-600 px-4 py-2 text-sm font-medium text-white hover:bg-ocean-700 disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-4 w-4 ${isMerging ? 'animate-spin' : ''}`} />
            {isMerging ? 'Merging...' : 'Merge Records'}
          </button>
        </div>
      )}
    </div>
  );
}

// ── Tab 3: Review & Fix ──────────────────────────────────────────────────────

function ReviewTab({
  records, pagination, isLoading, filter, setFilter, page, setPage,
  selectedRecord, setSelectedRecord, onAnalyzeGaps, isAnalyzing, workspace,
  onResolveConflict, isResolvingConflict,
}: any) {
  return (
    <div className="space-y-6">
      {/* Actions bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setFilter(''); setPage(1); }}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
              !filter ? 'bg-ocean-100 text-ocean-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            All ({workspace.totalRecords})
          </button>
          <button
            onClick={() => { setFilter('COMPLETE'); setPage(1); }}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
              filter === 'COMPLETE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Complete ({workspace.completeRecords})
          </button>
          <button
            onClick={() => { setFilter('HAS_GAPS'); setPage(1); }}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
              filter === 'HAS_GAPS' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Has Gaps ({workspace.gapRecords})
          </button>
          <button
            onClick={() => { setFilter('HAS_CONFLICTS'); setPage(1); }}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
              filter === 'HAS_CONFLICTS' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Conflicts ({workspace.conflictRecords})
          </button>
        </div>
        <button
          onClick={onAnalyzeGaps}
          disabled={isAnalyzing}
          className="inline-flex items-center gap-2 rounded-lg bg-ocean-600 px-4 py-2 text-sm font-medium text-white hover:bg-ocean-700 disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-4 w-4 ${isAnalyzing ? 'animate-spin' : ''}`} />
          {isAnalyzing ? 'Analyzing...' : 'Run Gap Analysis'}
        </button>
      </div>

      {/* Records table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="text-center py-12 text-gray-500">Loading records...</div>
        ) : records.length === 0 ? (
          <div className="text-center py-12">
            <TableCellsIcon className="h-10 w-10 text-gray-400 mx-auto mb-3" />
            <p className="text-sm text-gray-600">No records found. Upload and map source files first.</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Row</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Loan ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Missing</th>
                <th className="px-4 py-3 w-8"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {records.map((r: any) => {
                const fields = r.fields || {};
                return (
                  <tr
                    key={r.id}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => setSelectedRecord(selectedRecord?.id === r.id ? null : r)}
                  >
                    <td className="px-4 py-3 text-sm text-gray-600">{r.sourceRowNumber}</td>
                    <td className="px-4 py-3 text-sm font-mono text-gray-900">
                      {r.loanIdentifier ? r.loanIdentifier.substring(0, 20) + (r.loanIdentifier.length > 20 ? '...' : '') : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{fields['11'] || '-'}</td>
                    <td className="px-4 py-3">
                      <RecordStatusBadge status={r.status} />
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {r.missingFields?.length || 0}
                    </td>
                    <td className="px-4 py-3">
                      <ChevronRightIcon className={`h-4 w-4 text-gray-400 transition-transform ${selectedRecord?.id === r.id ? 'rotate-90' : ''}`} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {pagination && pagination.totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between bg-gray-50">
            <div className="text-xs text-gray-500">
              Page {pagination.page} of {pagination.totalPages} ({pagination.total} records)
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                className="rounded border border-gray-300 px-3 py-1 text-xs disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(Math.min(pagination.totalPages, page + 1))}
                disabled={page >= pagination.totalPages}
                className="rounded border border-gray-300 px-3 py-1 text-xs disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Record detail slide-out */}
      {selectedRecord && (
        <RecordDetail
          record={selectedRecord}
          onClose={() => setSelectedRecord(null)}
          onResolveConflict={onResolveConflict}
          isResolvingConflict={isResolvingConflict}
        />
      )}
    </div>
  );
}

// ── Record Status Badge ──────────────────────────────────────────────────────

function RecordStatusBadge({ status }: { status: string }) {
  switch (status) {
    case 'COMPLETE':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">
          <CheckCircleIcon className="h-3 w-3" /> Complete
        </span>
      );
    case 'HAS_GAPS':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700">
          <ExclamationTriangleIcon className="h-3 w-3" /> Has Gaps
        </span>
      );
    case 'HAS_CONFLICTS':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700">
          <XCircleIcon className="h-3 w-3" /> Conflicts
        </span>
      );
    case 'INVALID':
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700">
          <XCircleIcon className="h-3 w-3" /> Invalid
        </span>
      );
    default:
      return <StatusBadge variant="neutral">{status}</StatusBadge>;
  }
}

// ── Record Detail Panel ──────────────────────────────────────────────────────

function RecordDetail({ record, onClose, onResolveConflict, isResolvingConflict }: {
  record: any; onClose: () => void;
  onResolveConflict?: (recordId: string, fieldNumber: number, resolvedValue: string) => void;
  isResolvingConflict?: boolean;
}) {
  const fields = record.fields || {};
  const missingSet = new Set(record.missingFields || []);
  const autoSet = new Set(record.autoPopulated || []);
  const conflicts: { fieldNumber: number; sources: { system: string; value: string }[] }[] = record.conflicts || [];
  const conflictMap = new Map(conflicts.map((c) => [c.fieldNumber, c]));

  // Group fields by category
  const fieldGroups = [
    { label: 'Identification', fields: [1, 2, 3] },
    { label: 'Application & Loan', fields: [4, 5, 6, 7, 8, 9, 10, 11, 12] },
    { label: 'Geography', fields: [13, 14, 15, 16, 17, 18] },
    { label: 'Demographics', fields: [19, 25, 31, 32, 33, 39, 45, 46, 47, 48, 49, 50, 51, 52, 53] },
    { label: 'Financial', fields: [54, 55, 56, 57, 58, 59, 60, 62, 64, 69, 74, 76, 77, 78] },
    { label: 'Loan Features', fields: [80, 81, 82, 83, 84, 85, 86, 87] },
    { label: 'Application Channel', fields: [89, 90, 91] },
    { label: 'AUS', fields: [92, 98] },
    { label: 'Flags', fields: [104, 105, 106] },
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">
            Record Detail — Row {record.sourceRowNumber}
          </h3>
          {record.loanIdentifier && (
            <p className="text-xs text-gray-500 font-mono mt-0.5">{record.loanIdentifier}</p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <RecordStatusBadge status={record.status} />
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-sm">
            Close
          </button>
        </div>
      </div>

      {/* Conflicts section */}
      {conflicts.length > 0 && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <h4 className="text-sm font-semibold text-red-800 mb-3">
            {conflicts.length} Field Conflict{conflicts.length !== 1 ? 's' : ''} — Different source systems provided different values
          </h4>
          <div className="space-y-3">
            {conflicts.map((conflict) => (
              <ConflictResolver
                key={conflict.fieldNumber}
                conflict={conflict}
                recordId={record.id}
                currentValue={fields[String(conflict.fieldNumber)] || ''}
                onResolve={onResolveConflict}
                isResolving={isResolvingConflict}
              />
            ))}
          </div>
        </div>
      )}

      <div className="space-y-4 max-h-[60vh] overflow-y-auto">
        {fieldGroups.map((group) => (
          <div key={group.label}>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
              {group.label}
            </h4>
            <div className="grid grid-cols-2 gap-2">
              {group.fields.map((num) => {
                const value = fields[String(num)] || '';
                const isMissing = missingSet.has(num);
                const isAuto = autoSet.has(num);
                const hasConflict = conflictMap.has(num);
                const name = FIELD_NAMES[num] || `Field ${num}`;

                return (
                  <div
                    key={num}
                    className={`rounded-lg border p-2 text-xs ${
                      hasConflict ? 'border-red-300 bg-red-50' :
                      isMissing ? 'border-red-200 bg-red-50' :
                      isAuto ? 'border-blue-200 bg-blue-50' :
                      value ? 'border-gray-200' : 'border-gray-100 bg-gray-50'
                    }`}
                  >
                    <div className="text-gray-500 flex items-center justify-between">
                      <span>{num}. {name}</span>
                      {hasConflict && <span className="text-red-600 font-medium">Conflict</span>}
                      {isMissing && !hasConflict && <span className="text-red-500 font-medium">Missing</span>}
                      {isAuto && !hasConflict && <span className="text-blue-500 font-medium">Auto</span>}
                    </div>
                    <div className="font-mono text-gray-900 mt-0.5 truncate">
                      {value || <span className="text-gray-300">—</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Conflict Resolver ─────────────────────────────────────────────────────────

function ConflictResolver({ conflict, recordId, currentValue, onResolve, isResolving }: {
  conflict: { fieldNumber: number; sources: { system: string; value: string }[] };
  recordId: string;
  currentValue: string;
  onResolve?: (recordId: string, fieldNumber: number, resolvedValue: string) => void;
  isResolving?: boolean;
}) {
  const [manualValue, setManualValue] = useState('');
  const [showManual, setShowManual] = useState(false);
  const name = FIELD_NAMES[conflict.fieldNumber] || `Field ${conflict.fieldNumber}`;

  const SOURCE_LABELS: Record<string, string> = {
    JACK_HENRY_SILVERLAKE: 'Jack Henry',
    JACK_HENRY_CIF2020: 'Jack Henry CIF',
    JACK_HENRY_SYMITAR: 'Symitar',
    LASERPRO: 'LaserPro',
    FISERV_DNA: 'Fiserv DNA',
    FISERV_PRECISION: 'Fiserv',
    FIS_HORIZON: 'FIS Horizon',
    ENCOMPASS: 'Encompass',
    GENERIC_CSV: 'CSV',
    UNKNOWN: 'Unknown',
  };

  return (
    <div className="bg-white rounded-lg border border-red-200 p-3">
      <div className="text-xs font-medium text-gray-900 mb-2">
        {conflict.fieldNumber}. {name}
        <span className="text-gray-400 ml-2">Current: <span className="font-mono">{currentValue || '(empty)'}</span></span>
      </div>
      <div className="flex flex-wrap gap-2">
        {conflict.sources.map((src, i) => (
          <button
            key={i}
            onClick={() => onResolve?.(recordId, conflict.fieldNumber, src.value)}
            disabled={isResolving}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs hover:border-ocean-500 hover:bg-ocean-50 transition-colors disabled:opacity-50"
          >
            <span className="text-gray-500">{SOURCE_LABELS[src.system] || src.system}:</span>
            <span className="font-mono font-medium">{src.value}</span>
          </button>
        ))}
        {showManual ? (
          <div className="flex items-center gap-1">
            <input
              type="text"
              value={manualValue}
              onChange={(e) => setManualValue(e.target.value)}
              placeholder="Enter value..."
              className="rounded-lg border border-gray-300 px-2 py-1 text-xs w-32 focus:border-ocean-500 focus:ring-1 focus:ring-ocean-500"
            />
            <button
              onClick={() => {
                if (manualValue.trim()) {
                  onResolve?.(recordId, conflict.fieldNumber, manualValue.trim());
                  setShowManual(false);
                  setManualValue('');
                }
              }}
              disabled={isResolving || !manualValue.trim()}
              className="rounded-lg bg-ocean-600 px-2 py-1 text-xs text-white hover:bg-ocean-700 disabled:opacity-50"
            >
              Apply
            </button>
            <button
              onClick={() => { setShowManual(false); setManualValue(''); }}
              className="text-gray-400 hover:text-gray-600 text-xs px-1"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            onClick={() => setShowManual(true)}
            className="inline-flex items-center rounded-lg border border-dashed border-gray-300 px-3 py-1.5 text-xs text-gray-500 hover:border-gray-400 hover:text-gray-700"
          >
            Enter manually
          </button>
        )}
      </div>
    </div>
  );
}

// ── Tab 4: Auto-Populate ─────────────────────────────────────────────────────

function AutoPopulateTab({ workspace, onAutoPopulate, isPopulating, result }: any) {
  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Auto-Populate Fields</h3>
        <p className="text-sm text-gray-600 mb-6">
          Automatically fill in HMDA fields that can be derived from existing data.
        </p>

        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="text-sm font-medium text-gray-900">Geocoding</div>
            <div className="text-xs text-gray-500 mt-1">
              Fill county (field 17) and census tract (field 18) from property address data using FFIEC reference tables.
            </div>
          </div>
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="text-sm font-medium text-gray-900">Rate Spread</div>
            <div className="text-xs text-gray-500 mt-1">
              Calculate rate spread (field 55) from interest rate and APOR data for the matching loan term and date.
            </div>
          </div>
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="text-sm font-medium text-gray-900">ULI Check Digit</div>
            <div className="text-xs text-gray-500 mt-1">
              Generate the 2-digit ISO 7064 Mod 97 check digit for the Universal Loan Identifier (field 3).
            </div>
          </div>
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="text-sm font-medium text-gray-900">NA / Exempt Codes</div>
            <div className="text-xs text-gray-500 mt-1">
              Fill "NA" for fields not applicable to withdrawn/denied loans, and "Exempt" for partial exemption fields.
            </div>
          </div>
        </div>

        <button
          onClick={onAutoPopulate}
          disabled={isPopulating || workspace.totalRecords === 0}
          className="inline-flex items-center gap-2 rounded-lg bg-ocean-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-ocean-700 disabled:opacity-50"
        >
          <SparklesIcon className={`h-4 w-4 ${isPopulating ? 'animate-pulse' : ''}`} />
          {isPopulating ? 'Processing...' : 'Run Auto-Population'}
        </button>
      </div>

      {/* Results */}
      {result && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Results</h3>
          <div className="grid grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{result.geocoded}</div>
              <div className="text-xs text-gray-500">Geocoded</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{result.rateSpreadCalculated}</div>
              <div className="text-xs text-gray-500">Rate Spreads</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{result.uliGenerated}</div>
              <div className="text-xs text-gray-500">ULIs Generated</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{result.naExemptFilled}</div>
              <div className="text-xs text-gray-500">NA/Exempt Filled</div>
            </div>
          </div>
          {result.errors?.length > 0 && (
            <div className="mt-4 text-xs text-red-600">
              {result.errors.length} error(s) occurred during auto-population.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Tab 5: Monitor ────────────────────────────────────────────────────────────

function MonitorTab({ workspace, readiness, reminders, onSendReminders, isSendingReminders, onResolveReminder }: any) {
  const completionPercent = readiness?.completionPercent ?? 0;
  const daysUntil = readiness?.daysUntilDeadline ?? 0;
  const loReports: any[] = readiness?.loReports || [];

  return (
    <div className="space-y-6">
      {/* Readiness Dashboard */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Readiness Dashboard</h3>
        <div className="grid grid-cols-3 gap-4 mb-6">
          {/* Completion donut (simple CSS) */}
          <div className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-lg">
            <div className="relative h-24 w-24 mb-2">
              <svg viewBox="0 0 36 36" className="h-24 w-24">
                <path
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none" stroke="#e5e7eb" strokeWidth="3"
                />
                <path
                  d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                  fill="none" stroke="#059669" strokeWidth="3"
                  strokeDasharray={`${completionPercent}, 100`}
                  strokeLinecap="round"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xl font-bold text-gray-900">{completionPercent}%</span>
              </div>
            </div>
            <div className="text-xs text-gray-500">Completion</div>
          </div>

          {/* Deadline countdown */}
          <div className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-lg">
            <div className={`text-3xl font-bold ${daysUntil <= 30 ? 'text-red-600' : daysUntil <= 90 ? 'text-amber-600' : 'text-gray-900'}`}>
              {daysUntil}
            </div>
            <div className="text-xs text-gray-500 mt-1">Days Until Filing Deadline</div>
            <div className="text-xs text-gray-400 mt-0.5">
              March 1, {readiness?.filingYear ? readiness.filingYear + 1 : ''}
            </div>
          </div>

          {/* Record stats */}
          <div className="p-4 bg-gray-50 rounded-lg space-y-2">
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Complete</span>
              <span className="font-medium text-green-600">{workspace.completeRecords}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Has Gaps</span>
              <span className="font-medium text-amber-600">{workspace.gapRecords}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Conflicts</span>
              <span className="font-medium text-red-600">{workspace.conflictRecords}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-gray-500">Invalid</span>
              <span className="font-medium text-red-600">{workspace.invalidRecords}</span>
            </div>
            <div className="border-t border-gray-200 pt-2 flex justify-between text-xs">
              <span className="text-gray-700 font-medium">Total</span>
              <span className="font-bold text-gray-900">{workspace.totalRecords}</span>
            </div>
          </div>
        </div>
      </div>

      {/* LO Gap Report */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-gray-900">Loan Officer Gap Report</h3>
          <button
            onClick={() => onSendReminders(0)}
            disabled={isSendingReminders}
            className="inline-flex items-center gap-2 rounded-lg bg-ocean-600 px-4 py-2 text-sm font-medium text-white hover:bg-ocean-700 disabled:opacity-50"
          >
            <BellAlertIcon className={`h-4 w-4 ${isSendingReminders ? 'animate-pulse' : ''}`} />
            {isSendingReminders ? 'Sending...' : 'Send Reminders'}
          </button>
        </div>

        {loReports.length === 0 ? (
          <p className="text-sm text-gray-400">No records to analyze. Upload and map source files first.</p>
        ) : (
          <div className="overflow-hidden rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">NMLSR ID</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Complete</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Gaps</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Top Missing Fields</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loReports.map((lo: any, i: number) => (
                  <tr key={i} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm font-mono text-gray-900">
                      {lo.loanOfficerNmlsId || 'Unknown'}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-600">{lo.totalLoans}</td>
                    <td className="px-4 py-2 text-sm text-green-600">{lo.completeLoans}</td>
                    <td className="px-4 py-2 text-sm text-amber-600">{lo.gapLoans}</td>
                    <td className="px-4 py-2">
                      <div className="flex flex-wrap gap-1">
                        {lo.topMissingFields?.slice(0, 5).map((f: any) => (
                          <span key={f.fieldNumber} className="inline-block bg-amber-50 text-amber-700 rounded px-1.5 py-0.5 text-xs">
                            {f.fieldName} ({f.count})
                          </span>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Reminder History */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Reminder History</h3>

        {reminders.length === 0 ? (
          <p className="text-sm text-gray-400">No reminders sent yet.</p>
        ) : (
          <div className="overflow-hidden rounded-lg border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">LO</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Missing</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Level</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Sent</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {reminders.map((r: any) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-2 text-sm font-mono text-gray-900">
                      {r.loanOfficerId || 'Unknown'}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-600">
                      {r.missingFields?.length || 0} fields
                    </td>
                    <td className="px-4 py-2">
                      <span className={`inline-block rounded px-1.5 py-0.5 text-xs font-medium ${
                        r.level === 0 ? 'bg-blue-100 text-blue-700' :
                        r.level === 1 ? 'bg-amber-100 text-amber-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {r.level === 0 ? 'Initial' : r.level === 1 ? '30-Day' : '60-Day'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-500">
                      {r.sentAt ? new Date(r.sentAt).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-2">
                      {r.resolvedAt ? (
                        <span className="inline-flex items-center gap-1 text-xs text-green-600">
                          <CheckCircleIcon className="h-3 w-3" /> Resolved
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                          <ExclamationTriangleIcon className="h-3 w-3" /> Pending
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      {!r.resolvedAt && (
                        <button
                          onClick={() => onResolveReminder(r.id)}
                          className="text-xs text-ocean-600 hover:text-ocean-700 font-medium"
                        >
                          Resolve
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Tab 6: Export ─────────────────────────────────────────────────────────────

function ExportTab({ workspace, onExport, isExporting, onUpdateTs, validationData, onValidate, isValidating, onFairLending, isFairLending }: any) {
  const ts = (workspace.transmittalSheet || {}) as Record<string, string>;
  const [tsData, setTsData] = useState({
    institutionName: ts.institutionName || '',
    lei: ts.lei || '',
    taxId: ts.taxId || '',
    contactName: ts.contactName || '',
    contactPhone: ts.contactPhone || '',
    contactEmail: ts.contactEmail || '',
    contactStreetAddress: ts.contactStreetAddress || '',
    contactCity: ts.contactCity || '',
    contactState: ts.contactState || '',
    contactZipCode: ts.contactZipCode || '',
    agencyCode: ts.agencyCode || '',
    calendarQuarter: ts.calendarQuarter || '4',
  });

  const handleTsChange = (field: string, value: string) => {
    setTsData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <div className="space-y-6">
      {/* Transmittal Sheet form */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Transmittal Sheet</h3>
        <p className="text-sm text-gray-600 mb-4">
          The transmittal sheet is the first row of your LAR file. These fields are pre-populated
          from your institution setup.
        </p>
        <div className="grid grid-cols-2 gap-4">
          <TsField label="Institution Name" value={tsData.institutionName} onChange={(v) => handleTsChange('institutionName', v)} />
          <TsField label="LEI (20-char)" value={tsData.lei} onChange={(v) => handleTsChange('lei', v)} />
          <TsField label="Tax ID (99-9999999)" value={tsData.taxId} onChange={(v) => handleTsChange('taxId', v)} />
          <TsField label="Contact Name" value={tsData.contactName} onChange={(v) => handleTsChange('contactName', v)} />
          <TsField label="Contact Phone" value={tsData.contactPhone} onChange={(v) => handleTsChange('contactPhone', v)} />
          <TsField label="Contact Email" value={tsData.contactEmail} onChange={(v) => handleTsChange('contactEmail', v)} />
          <TsField label="Street Address" value={tsData.contactStreetAddress} onChange={(v) => handleTsChange('contactStreetAddress', v)} />
          <TsField label="City" value={tsData.contactCity} onChange={(v) => handleTsChange('contactCity', v)} />
          <TsField label="State" value={tsData.contactState} onChange={(v) => handleTsChange('contactState', v)} />
          <TsField label="Zip Code" value={tsData.contactZipCode} onChange={(v) => handleTsChange('contactZipCode', v)} />
        </div>
        <div className="mt-4">
          <button
            onClick={() => onUpdateTs(tsData)}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Save Transmittal Sheet
          </button>
        </div>
      </div>

      {/* Export summary */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-4">Export LAR File</h3>
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="text-center p-3 bg-gray-50 rounded-lg">
            <div className="text-xl font-bold text-gray-900">{workspace.totalRecords}</div>
            <div className="text-xs text-gray-500">Total Records</div>
          </div>
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <div className="text-xl font-bold text-green-600">{workspace.completeRecords}</div>
            <div className="text-xs text-gray-500">Complete</div>
          </div>
          <div className="text-center p-3 bg-amber-50 rounded-lg">
            <div className="text-xl font-bold text-amber-600">{workspace.gapRecords}</div>
            <div className="text-xs text-gray-500">With Gaps</div>
          </div>
          <div className="text-center p-3 bg-red-50 rounded-lg">
            <div className="text-xl font-bold text-red-600">{workspace.invalidRecords}</div>
            <div className="text-xs text-gray-500">Invalid</div>
          </div>
        </div>

        {workspace.gapRecords > 0 && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            {workspace.gapRecords} record(s) still have missing fields. The LAR file will be generated
            with empty values for missing fields. Consider running auto-population or filling gaps manually first.
          </div>
        )}

        <button
          onClick={onExport}
          disabled={isExporting || workspace.totalRecords === 0}
          className="inline-flex items-center gap-2 rounded-lg bg-ocean-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-ocean-700 disabled:opacity-50"
        >
          <DocumentArrowDownIcon className="h-4 w-4" />
          {isExporting ? 'Generating...' : 'Generate & Download LAR File'}
        </button>
        <p className="text-xs text-gray-500 mt-2">
          Downloads a pipe-delimited .txt file ready for CFPB submission.
        </p>
      </div>

      {/* Edit Checks / Validation */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">CFPB Edit Checks</h3>
        <p className="text-sm text-gray-600 mb-4">
          Run the CFPB's syntactical, validity, quality, and macro edit checks against your assembled LAR data
          before filing.
        </p>

        <button
          onClick={onValidate}
          disabled={isValidating || workspace.totalRecords === 0}
          className="inline-flex items-center gap-2 rounded-lg border border-ocean-600 text-ocean-600 px-4 py-2 text-sm font-medium hover:bg-ocean-50 disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-4 w-4 ${isValidating ? 'animate-spin' : ''}`} />
          {isValidating ? 'Running Edit Checks...' : 'Run Edit Checks'}
        </button>

        {/* Validation results */}
        {validationData?.summary && (
          <div className="mt-4">
            <div className="grid grid-cols-4 gap-3 mb-4">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-xl font-bold text-gray-900">{validationData.summary.totalIssues}</div>
                <div className="text-xs text-gray-500">Total Issues</div>
              </div>
              <div className={`text-center p-3 rounded-lg ${validationData.summary.syntacticalErrors > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
                <div className={`text-xl font-bold ${validationData.summary.syntacticalErrors > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {validationData.summary.syntacticalErrors}
                </div>
                <div className="text-xs text-gray-500">Syntactical</div>
              </div>
              <div className={`text-center p-3 rounded-lg ${validationData.summary.validityErrors > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
                <div className={`text-xl font-bold ${validationData.summary.validityErrors > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {validationData.summary.validityErrors}
                </div>
                <div className="text-xs text-gray-500">Validity</div>
              </div>
              <div className={`text-center p-3 rounded-lg ${validationData.summary.qualityWarnings > 0 ? 'bg-amber-50' : 'bg-green-50'}`}>
                <div className={`text-xl font-bold ${validationData.summary.qualityWarnings > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                  {validationData.summary.qualityWarnings}
                </div>
                <div className="text-xs text-gray-500">Quality</div>
              </div>
            </div>

            {validationData.summary.totalIssues === 0 ? (
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                <CheckCircleIcon className="h-5 w-5 text-green-600" />
                All edit checks passed. Your LAR file is ready for CFPB submission.
              </div>
            ) : (
              <>
                {validationData.topFailures?.length > 0 && (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
                      <span className="text-xs font-semibold text-gray-600 uppercase">Top Edit Failures</span>
                    </div>
                    <div className="divide-y divide-gray-100">
                      {validationData.topFailures.map((f: any, i: number) => (
                        <div key={i} className="px-4 py-2 flex items-center justify-between text-xs">
                          <div>
                            <span className={`inline-block rounded px-1.5 py-0.5 font-medium mr-2 ${
                              f.editType === 'syntactical' ? 'bg-red-100 text-red-700' :
                              f.editType === 'validity' ? 'bg-red-100 text-red-700' :
                              'bg-amber-100 text-amber-700'
                            }`}>
                              {f.editId}
                            </span>
                            <span className="text-gray-700">{f.description || f.editId}</span>
                          </div>
                          <span className="text-gray-500 font-mono">
                            {f.affectedRows} row{f.affectedRows !== 1 ? 's' : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Fair Lending Analysis */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Fair Lending Analysis</h3>
        <p className="text-sm text-gray-600 mb-4">
          Run denial disparity and pricing analysis on your assembled LAR data to identify potential
          fair lending risks before filing.
        </p>

        <button
          onClick={onFairLending}
          disabled={isFairLending || workspace.totalRecords === 0 || !validationData?.validationId}
          className="inline-flex items-center gap-2 rounded-lg border border-ocean-600 text-ocean-600 px-4 py-2 text-sm font-medium hover:bg-ocean-50 disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-4 w-4 ${isFairLending ? 'animate-spin' : ''}`} />
          {isFairLending ? 'Analyzing...' : 'Run Fair Lending Analysis'}
        </button>

        {!validationData?.validationId && workspace.totalRecords > 0 && (
          <p className="text-xs text-gray-400 mt-2">
            Run edit checks first to enable fair lending analysis.
          </p>
        )}

        {validationData?.fairLendingId && (
          <div className="mt-3 flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
            <CheckCircleIcon className="h-5 w-5 text-green-600" />
            Fair lending analysis complete. View detailed results on the Fair Lending page.
          </div>
        )}
      </div>
    </div>
  );
}

function TsField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg border border-gray-300 px-3 py-1.5 text-sm focus:border-ocean-500 focus:ring-1 focus:ring-ocean-500"
      />
    </div>
  );
}
